import { Tooltip } from 'antd';
import { Building2, ShieldCheck, UserPlus } from 'lucide-react';
import type { ReactNode } from 'react';
import { SOVEREIGN } from '../../utils/epcProjectVisual';

export interface EpcTeamHeaderProps {
  /** Uppercase eyebrow, e.g. "PMO · Team". */
  eyebrow: string;
  /** Project name; falls back to a generic title when absent. */
  projectName?: string;
  /** One-line description under the title. */
  subtitle: string;
  deptCount: number;
  memberCount: number;
  /** depts-without-head + members-with-conflicts. Stat renders only when > 0. */
  attentionCount: number;
  onAttentionClick?: () => void;
  /** When provided, the "+ Member" button renders. */
  onAddMember?: () => void;
  /** When provided, the "+ Department" button renders. */
  onAddDepartment?: () => void;
}

/** Engraved-register tooltip body: champagne caption + cream explanation. */
function StatHint({ label, body }: { label: string; body: string }) {
  return (
    <div style={{ maxWidth: 264 }}>
      <p
        style={{
          margin: 0,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: SOVEREIGN.champagne,
        }}
      >
        {label}
      </p>
      <p style={{ margin: '5px 0 0', fontSize: 12, lineHeight: 1.55, color: 'rgba(250,247,238,0.94)' }}>
        {body}
      </p>
    </div>
  );
}

/** Typography-only KPI — no tile, no box; weight + ink carry the hierarchy. */
function HeaderStat({
  value,
  label,
  hint,
  alert = false,
  onClick,
}: {
  value: number;
  label: string;
  hint: ReactNode;
  alert?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <div
        className={`text-[27px] font-extrabold leading-none tracking-tight tabular-nums ${
          alert ? 'text-[#8A5A08]' : 'text-[#0B3B2E]'
        }`}
      >
        {value}
      </div>
      <div
        className={`mt-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] transition-colors ${
          alert ? 'text-[#8A5A08]' : 'text-[#7C8A82] group-hover:text-[#1F5A47]'
        }`}
      >
        {alert ? (
          <span
            aria-hidden="true"
            className="inline-block h-1.5 w-1.5 rounded-full motion-safe:animate-pulse"
            style={{ background: SOVEREIGN.champagne }}
          />
        ) : null}
        <span className="underline-offset-4 decoration-dotted decoration-[#C9A961] group-hover:underline">
          {label}
        </span>
      </div>
    </>
  );

  /* A disabled <button> swallows mouse events, which would mute the tooltip —
     so only interactive stats render as buttons. */
  return (
    <Tooltip title={hint} color={SOVEREIGN.title} placement="bottom" mouseEnterDelay={0.15}>
      {onClick ? (
        <button type="button" onClick={onClick} className="group cursor-help text-left">
          {body}
        </button>
      ) : (
        <div className="group cursor-help">{body}</div>
      )}
    </Tooltip>
  );
}

function StatDivider() {
  return (
    <span
      aria-hidden="true"
      className="hidden w-px self-stretch sm:block"
      style={{ background: 'rgba(11,59,46,0.15)' }}
    />
  );
}

export function EpcTeamHeader({
  eyebrow,
  projectName,
  subtitle,
  deptCount,
  memberCount,
  attentionCount,
  onAttentionClick,
  onAddMember,
  onAddDepartment,
}: EpcTeamHeaderProps) {
  const attentionBody =
    attentionCount === 1
      ? '1 department has no active Department Head. Until a head is assigned there, managers cannot be added and the reporting & approval chain stays incomplete. Assign a head from the org chart below.'
      : `${attentionCount} departments have no active Department Head. Until heads are assigned, managers cannot be added there and the reporting & approval chains stay incomplete. Assign heads from the org chart below.`;

  return (
    <div
      className="relative rounded-2xl p-4 sm:p-5"
      style={{
        background: SOVEREIGN.cardBg,
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        boxShadow: SOVEREIGN.cardShadow,
      }}
    >
      {/* engraved banknote rule */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute"
        style={{ inset: 7, border: `1px dashed ${SOVEREIGN.innerRule}`, borderRadius: 11 }}
      />

      <div className="relative flex flex-wrap items-center gap-x-5 gap-y-3">
        {/* identity */}
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={{
              background: 'rgba(201,169,97,0.14)',
              border: '1px solid rgba(11,59,46,0.20)',
              color: SOVEREIGN.title,
            }}
          >
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#1F5A47]">{eyebrow}</p>
            <p className="truncate text-lg font-extrabold tracking-tight text-[#0B3B2E]">
              {projectName ?? 'Team & reporting lines'}
            </p>
            <p className="text-xs text-[#7C8A82]">{subtitle}</p>
          </div>
        </div>

        {/* stats — same row as the title, typography only, hairline dividers */}
        <div className="ml-auto flex flex-wrap items-stretch gap-x-6 gap-y-2">
          <HeaderStat
            value={deptCount}
            label="Departments"
            hint={
              <StatHint
                label="Departments"
                body="Active departments (org units) in this project. Each department groups a head, its managers and site engineers into one reporting line. Removed departments are not counted."
              />
            }
          />
          <StatDivider />
          <HeaderStat
            value={memberCount}
            label="Team members"
            hint={
              <StatHint
                label="Team members"
                body="Unique people holding an active assignment on this project — governance, department heads, managers and site engineers. A person working across several departments is counted once."
              />
            }
          />
          {attentionCount > 0 ? (
            <>
              <StatDivider />
              <HeaderStat
                value={attentionCount}
                label="Need attention"
                alert
                onClick={onAttentionClick}
                hint={<StatHint label="Need attention" body={attentionBody} />}
              />
            </>
          ) : null}
        </div>

        {/* actions */}
        {(onAddDepartment || onAddMember) ? (
          <div className="flex gap-2">
            {onAddDepartment ? (
              <button
                type="button"
                onClick={onAddDepartment}
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-colors"
                style={{
                  background: SOVEREIGN.inputBg,
                  border: `1px solid ${SOVEREIGN.inputBorder}`,
                  color: SOVEREIGN.title,
                }}
              >
                <Building2 className="h-3.5 w-3.5" aria-hidden="true" /> Department
              </button>
            ) : null}
            {onAddMember ? (
              <button
                type="button"
                onClick={onAddMember}
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-[#2d5c47]"
                style={{
                  background: SOVEREIGN.btn,
                  border: `1px solid ${SOVEREIGN.btnBorder}`,
                  boxShadow: SOVEREIGN.btnShadow,
                }}
              >
                <UserPlus className="h-3.5 w-3.5" aria-hidden="true" /> Member
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

    </div>
  );
}
